import React, { useCallback } from 'react';
import isEqual from 'lodash/isEqual';

import { Button } from 'src/presentation/components/agent_desktop_components';
import { FooterLarge } from 'src/presentation/components/forms/Footer';
import { FormSection, Description } from 'src/presentation/components/forms/Layout';
import { StyledBody } from 'src/presentation/components/forms/Body';
import Checkbox from 'src/presentation/components/forms/Checkbox';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import Form, { useForm, useStandardRepoOnSave } from 'src/presentation/components/forms/Form';
import messagingResource from 'src/actions/messaging/MessagingResource';
import { initiateFBLogin } from 'src/presentation/components/pages/social/components/FBSDKIframeLoader';

const isEqualHelper = (prev, next) => {
  const prevState = {
    enableOutboundInstagramMessageEcho: prev.enableOutboundInstagramMessageEcho,
    handleInstagramStoryMentions: prev.handleInstagramStoryMentions,
    handleInstagramComments: prev.handleInstagramComments,
  };
  const currentState = {
    enableOutboundInstagramMessageEcho: next.enableOutboundInstagramMessageEcho,
    handleInstagramStoryMentions: next.handleInstagramStoryMentions,
    handleInstagramComments: next.handleInstagramComments,
  };
  return isEqual(currentState, prevState);
};

const InstagramConfigurationsSettingsForm = ({
  errors,
  iframeRef,
  isInstagramDirectAccountUpdateRequired,
  item,
  onClose,
  targetOrigin,
}) => {
  const resetErrors = messagingResource.actions.resetErrors;
  const saveHandler = useStandardRepoOnSave({ updateSuccessToastMessage: 'Updated Instagram Settings' });
  const formContext = useForm({
    item,
    errors,
    resetErrors,
    onSave: saveHandler,
    update: messagingResource.effects.replace,
    validate: messagingResource.effects.validate,
  });

  const { onSave, workingState } = formContext;

  const handleSave = useCallback(() => {
    if (workingState.handleInstagramComments && isInstagramDirectAccountUpdateRequired) {
      initiateFBLogin({ iframeRef, targetOrigin });
    }
    onSave();
    onClose();
  }, [isInstagramDirectAccountUpdateRequired, iframeRef, onClose, onSave, targetOrigin, workingState]);

  const isStateInitial = isEqualHelper(item, workingState);

  return (
    <Form context={formContext}>
      <StyledBody data-aid="editor">
        <Description>
          How would you like Gladly to handle Instagram? Here&apos;s some{` `}
          <ExternalLink
            data-aid="help-docs-link"
            label="best practices"
            to="https://help.gladly.com/docs/instagram-best-practices"
          />
          {` `}to help decide.
        </Description>
        <FormSection>
          <Checkbox
            data-aid="enableOutboundInstagramMessageEcho"
            attr="enableOutboundInstagramMessageEcho"
            label="Show direct messages initiated by all non-Gladly users in the Conversation Timeline"
          />
        </FormSection>
        <FormSection>
          <Checkbox
            label="Show all Instagram Story mentions in the Conversation Timeline"
            data-aid="handleInstagramStoryMentions"
            attr="handleInstagramStoryMentions"
          />
        </FormSection>
        <FormSection>
          <Checkbox
            label="Handle Instagram comments in the Conversation Timeline"
            data-aid="handleInstagramComments"
            attr="handleInstagramComments"
          />
        </FormSection>
      </StyledBody>
      <FooterLarge>
        <Button type={Button.Types.TEXT} data-aid="modalCancel" onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid="editorSave" onClick={handleSave} disabled={isStateInitial}>
          Save
        </Button>
      </FooterLarge>
    </Form>
  );
};

export default InstagramConfigurationsSettingsForm;
